import { debounce, isFunction, memoizeFunction } from '@agilepoint/utils-common';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Editor } from 'roosterjs-editor-core';
import { CutPasteListChain, HyperLink, ImageEdit, Paste, TableResize } from 'roosterjs-editor-plugins';
import { PluginEvent, PluginEventType, IEditor, ContentPosition } from 'roosterjs-editor-types';
import styled, { css } from 'styled-components';
import { IRichTextEditorProps } from './rich-text-editor.types';
import { EditorToolbar } from './action-toolbar';
import { RichTextEditorState } from './theme';
import { getStyles } from './utils';
import { EditorContainer } from './parts';
import { ChangeDetectionPlugin, getChangeDetector } from './plugins';
import { useChooseRef } from '../../utils';

const getRichTextEditorContainerStyleByState = memoizeFunction((state: RichTextEditorState) => css`
        border-color: ${({ theme }) => getStyles(theme, state).borderColor};
    `);

const getRichTextEditorContainerStyle = memoizeFunction((state: RichTextEditorState) =>
    ({ disabled, error }: IRichTextEditorProps) => getRichTextEditorContainerStyleByState(disabled ? 'disabled' : (error ? 'error' : state)));

const TRIGGER_CHANGE_EVENT_LIST: PluginEventType[] = [PluginEventType.ContentChanged, PluginEventType.KeyUp];

function RichTextEditorBase(props: IRichTextEditorProps) {
    const { className, elementRef, disabled, error, defaultValue, value, onValueChange, parseDroppedContent } = props;
    const editorContainerRef = useChooseRef<HTMLDivElement>(elementRef);
    const prevContentRef = useRef(defaultValue);
    const [editor, setEditorInstance] = useState<IEditor>();
    const changeDetectionPlugin = useMemo(() => new ChangeDetectionPlugin(), []);

    const onChange = useMemo(() => debounce((ev: PluginEvent) => {
        if (!editor) {
            return;
        }
        if (!TRIGGER_CHANGE_EVENT_LIST.includes(ev.eventType) || !isFunction(onValueChange)) {
            return;
        }
        const content = editor.getContent();
        if (prevContentRef.current === content) {
            return;
        }
        prevContentRef.current = content;
        onValueChange(content);
    }, 50), [editor, onValueChange]);

    const changeDetector = getChangeDetector(changeDetectionPlugin);

    const onDrop = useCallback((ev: Event) => {
        const data = (ev as DragEvent).dataTransfer?.getData('text/plain');
        if (!data || !isFunction(parseDroppedContent) || !editor) {
            return;
        }
        ev.preventDefault();
        const newContent = parseDroppedContent(data);
        editor.insertContent(newContent, { position: ContentPosition.SelectionStart, replaceSelection: true, updateCursor: true });
    }, [editor, parseDroppedContent]);

    useEffect(() => {
        changeDetector.subscribe(onChange);
        editor?.addDomEventHandler('drop', onDrop);
        return () => {
            changeDetector.unsubscribe(onChange);
        };
    }, [changeDetector, editor, onChange, onDrop]);

    useEffect(() => {
        const { current: container } = editorContainerRef;
        if (!container || editor) {
            return;
        }
        setEditorInstance(new Editor(container, {
            plugins: [
                changeDetectionPlugin,
                new CutPasteListChain(),
                new HyperLink(),
                new ImageEdit(),
                new Paste(),
                new TableResize()
            ],
            initialContent: value ?? defaultValue,
            defaultFormat: { fontFamily: 'roboto', fontSize: '11pt' }
        }));
    }, [changeDetectionPlugin, defaultValue, editor, editorContainerRef, value]);

    return (
        <div className={className}>
            <EditorToolbar editor={editor} hidden={disabled} changeDetector={changeDetector} />
            <EditorContainer ref={editorContainerRef} $disabled={disabled} $error={error} contentEditable={!disabled} />
        </div>
    );
}

export const RichTextEditor = styled(RichTextEditorBase).attrs({
    className: String('ap-richTextEditor')
})`
    width: 100%;
    height: 100%;
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
    transition: all 0.15s ease-in-out;
    display: grid;
    grid-template-rows: ${({ disabled }) => (disabled ? '1fr' : 'auto minmax(0,1fr)')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'text')};
    border-radius: ${({ theme: { maskedInput: { composites } } }) => composites.borderRadius};
    border-style: ${({ theme: { maskedInput: { composites } } }) => composites.borderStyle};
    border-width: ${({ theme: { maskedInput: { composites } } }) => composites.borderWidth};
    padding: 0;
    ${getRichTextEditorContainerStyle('default')}
    &:hover {
        ${getRichTextEditorContainerStyle('hovered')};
    }
    &:focus-within, :active {
        ${getRichTextEditorContainerStyle('focussed')};
    };
`;
